/**
 * <predictive-search> — typeahead for the header search box.
 *
 * As the shopper types, it asks window.ValerydCards.suggest() (assets/product-resolver.js,
 * Shopify's predictive-search endpoint, exact SKU first) and shows a dropdown of
 * product suggestions with a thumbnail, title, art-no (SKU), price and price excl. VAT. A query shaped like a plate or a VIN
 * is looked up in parallel through ValerydFitment (assets/fitment.js), which owns
 * the App Proxy call and caches a match for the results page it links to. A brake ID or an OE
 * number is on no product, so the store suggests nothing for it; only then, and only for a
 * query shaped like a part number, EasySearch is asked, and a match becomes one row — type and
 * part count — that leads straight to the parts on the store's results page (Åke 2026-09-29).
 * An article number the store finds costs no proxy call. Keyboard navigable (↓/↑/Enter/Esc),
 * closes on outside click. With JS, submitting follows the rendered "all results"
 * destination without repeating the predictive request; without JS, the form still
 * falls back to /search.
 *
 * Built with DOM nodes + textContent (no innerHTML) so suggestion data can't
 * inject markup. Guarded define + listener cleanup in disconnectedCallback.
 */
if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-predictive-results]');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.currency = this.dataset.currency || 'EUR';
        this.locale = this.dataset.locale || 'en';
        this.minChars = 2;
        this.active = -1;
        this.controller = null;
        this.loadingController = null;
        this.pendingQuery = null;
        this.pendingSearch = null;
        this.renderedQuery = null;
        if (!this.input || !this.results) return;

        this.onInput = this.debounce(() => this.search(), 200);
        this.onFocus = () => {
          if (this.input.value.trim().length >= this.minChars && this.results.childElementCount) this.open();
        };
        this.onKeydown = (event) => this.handleKey(event);
        this.onSubmit = (event) => this.submit(event);
        this.onDocClick = (event) => {
          if (!this.contains(event.target)) this.close();
        };

        this.input.setAttribute('role', 'combobox');
        this.input.setAttribute('aria-autocomplete', 'list');
        this.input.setAttribute('aria-haspopup', 'listbox');
        this.input.setAttribute('aria-expanded', 'false');

        this.input.addEventListener('input', this.onInput);
        this.input.addEventListener('focus', this.onFocus);
        if (this.form) this.form.addEventListener('submit', this.onSubmit);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('click', this.onDocClick);
      }

      disconnectedCallback() {
        if (this.input) {
          this.input.removeEventListener('input', this.onInput);
          this.input.removeEventListener('focus', this.onFocus);
        }
        if (this.form) this.form.removeEventListener('submit', this.onSubmit);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('click', this.onDocClick);
        clearTimeout(this.debounceTimer);
        if (this.controller) this.controller.abort();
        this.pendingQuery = null;
        this.pendingSearch = null;
      }

      debounce(fn, wait) {
        return (...args) => {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => fn.apply(this, args), wait);
        };
      }

      submit(event) {
        clearTimeout(this.debounceTimer);
        this.followAllResults(event);
      }

      followAllResults(event) {
        const q = this.input.value.trim();
        const allResults =
          this.renderedQuery === q && this.results ? this.results.querySelector('.predictive__all') : null;
        if (!allResults) return false;
        event.preventDefault();
        allResults.click();
        return true;
      }

      setLoading(loading) {
        if (!this.submitButton) return;
        this.submitButton.disabled = loading;
        this.submitButton.setAttribute('aria-busy', loading ? 'true' : 'false');
      }

      search() {
        const q = this.input.value.trim();
        if (q.length < this.minChars) {
          if (this.controller) this.controller.abort();
          this.controller = null;
          this.loadingController = null;
          this.pendingQuery = null;
          this.pendingSearch = null;
          this.renderedQuery = null;
          this.setLoading(false);
          return this.close();
        }

        // Repeated input events for the same value reuse the active request instead of
        // aborting and immediately issuing the exact same requests again.
        if (this.pendingSearch && this.pendingQuery === q) return this.pendingSearch;

        // If this exact query has already rendered, simply reopen its dropdown without
        // flashing another loading state.
        if (this.renderedQuery === q) {
          this.open();
          return Promise.resolve();
        }

        if (this.controller) this.controller.abort();
        const controller = new AbortController();
        this.controller = controller;
        this.loadingController = controller;
        this.setLoading(true);
        this.pendingQuery = q;
        const pending = this.performSearch(q, controller);
        this.pendingSearch = pending;
        pending.finally(() => {
          if (this.pendingSearch === pending) {
            this.pendingSearch = null;
            this.pendingQuery = null;
          }
        });
        return pending;
      }

      async performSearch(q, controller) {
        let products = [];
        let trailerMatch = null;
        const fitment = window.ValerydFitment;
        const regnr = fitment ? fitment.normalise(q) : q;
        // `country` picks the registry AND the label language, and 401s on anything off
        // its list. It used to be fed localization.country — the market's default, AT for
        // a Swedish visitor — so every header reg-no lookup failed. It now carries a
        // country that resolves: the visitor's own once known, SE (the module's default)
        // until then.
        const lang = this.dataset.country || '';
        // The trailer's own country, for the badge and the saved record. Unknown until
        // regnr-country.js has resolved it, and left empty rather than guessed — a
        // default of 'SE' here is what let a language value masquerade as a country.
        const plateCountry = (document.documentElement && document.documentElement.dataset.regnrCountry) || '';
        try {
          // Already ordered: exact SKU, then exact handle, then the platform's relevance.
          const productRequest = window.ValerydCards.suggest(q, { signal: controller.signal });

          const trailerRequest = fitment && fitment.looksLikeIdentifier(q)
            ? (async () => {
                const result = await fitment.lookup(regnr, { country: lang, signal: controller.signal });
                if (result.kind === 'aborted') {
                  const error = new Error('aborted');
                  error.name = 'AbortError';
                  throw error;
                }
                if (!result.ok) throw new Error('Registration lookup failed: ' + result.kind);
                if (!fitment.matchesIdentifier(result.data, regnr)) return null;
                return { country: plateCountry, regnr: regnr, trailer: result.data.trailer };
              })()
            : Promise.resolve(null);

          const [productResult, trailerResult] = await Promise.allSettled([productRequest, trailerRequest]);
          if (controller.signal.aborted) return;
          // Some fetch implementations can still resolve after abort. The exact
          // query guard prevents an older response from replacing newer results.
          if (this.input.value.trim() !== q) return;
          products = productResult.status === 'fulfilled' ? productResult.value : [];
          trailerMatch = trailerResult.status === 'fulfilled' ? trailerResult.value : null;
          let providerFailed = productResult.status === 'rejected' || trailerResult.status === 'rejected';
          let partMatch = null;
          if (!products.length && !trailerMatch && !providerFailed && fitment && fitment.looksLikePartIdentifier(q)) {
            try {
              partMatch = await this.partLookup(q, lang, controller.signal);
            } catch (error) {
              if (controller.signal.aborted || error.name === 'AbortError') return;
              providerFailed = true;
            }
            if (controller.signal.aborted || this.input.value.trim() !== q) return;
          }
          if (providerFailed && !products.length && !trailerMatch && !partMatch) {
            this.renderError();
            return;
          }
          this.render(products, q, trailerMatch, partMatch);
          this.renderedQuery = q;
        } catch (error) {
          if (controller.signal.aborted || error.name === 'AbortError') return;
          if (this.input.value.trim() !== q) return;
          this.renderError();
        } finally {
          if (this.controller === controller) this.controller = null;
          if (this.loadingController === controller) {
            this.loadingController = null;
            this.setLoading(false);
          }
        }
      }

      // One row for what EasySearch knows the query as — "TSV223P", "Brake ID · 46 parts" —
      // linking to the parts on the store's results page rather than to a second lookup.
      async partLookup(q, country, signal) {
        const fitment = window.ValerydFitment;
        const result = await fitment.lookup(q, { country: country, signal: signal });
        if (result.kind === 'aborted') {
          const error = new Error('aborted');
          error.name = 'AbortError';
          throw error;
        }
        if (!result.ok) throw new Error('Part lookup failed: ' + result.kind);
        const data = result.data;
        if (!data || data.trailer || !fitment.hasIdentifierMatch(data)) return null;
        const skus = fitment.skus(data);
        if (!skus.length) return null;
        const groups = fitment.groups(data);
        const typeOf = (group) => (group.searchTypeKey && this.dataset['type' + group.searchTypeKey.charAt(0).toUpperCase() + group.searchTypeKey.slice(1)]) || '';
        const group = groups.find(typeOf) || groups[0];
        const count = ((skus.length === 1 ? this.dataset.partCountOne : this.dataset.partCount) || '').replace('%COUNT%', skus.length);
        return {
          term: group.searchFor || q,
          meta: [typeOf(group), count].filter(Boolean).join(' · '),
          href: window.ValerydCards.searchUrl(skus, q) || ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'search?q=' + encodeURIComponent(q),
        };
      }

      render(products, q, trailerMatch, partMatch) {
        this.setActive(-1);
        const frag = document.createDocumentFragment();

        if (!products.length && !trailerMatch && !partMatch) {
          this.input.removeAttribute('aria-controls');
          const empty = document.createElement('p');
          empty.className = 'predictive__empty';
          empty.setAttribute('role', 'status');
          empty.textContent = (this.dataset.noResults || 'No matches').replace('%TERMS%', q);
          frag.appendChild(empty);
          this.results.replaceChildren(frag);
          this.open();
          return;
        }

        const list = document.createElement('ul');
        list.className = 'predictive__list';
        list.id = `${this.results.id || 'predictive-results'}-listbox`;
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-label', this.results.dataset.label || 'Search results');
        this.input.setAttribute('aria-controls', list.id);
        if (trailerMatch) list.appendChild(this.trailerItem(trailerMatch, 0));
        if (partMatch) list.appendChild(this.partItem(partMatch, 0));
        const productOffset = trailerMatch || partMatch ? 1 : 0;
        products.forEach((product, index) => list.appendChild(this.item(product, index + productOffset)));
        frag.appendChild(list);

        const all = document.createElement('a');
        all.className = 'predictive__all';
        const resultQuery = trailerMatch ? trailerMatch.regnr : q;
        all.href = partMatch ? partMatch.href : ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'search?q=' + encodeURIComponent(resultQuery) + (trailerMatch ? '' : '&type=product');
        all.textContent = (
          trailerMatch || partMatch ? this.dataset.viewTrailer || 'See all matching parts' : this.dataset.viewAll || 'See all results'
        ).replace('%TERMS%', resultQuery);
        frag.appendChild(all);

        this.results.replaceChildren(frag);
        this.open();
      }

      renderError() {
        this.setActive(-1);
        this.input.removeAttribute('aria-controls');
        const error = document.createElement('p');
        error.className = 'predictive__empty';
        error.setAttribute('role', 'status');
        error.textContent = this.dataset.error || 'Search service temporarily unavailable.';
        this.results.replaceChildren(error);
        this.open();
      }

      trailerItem(match, index) {
        const li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        const link = document.createElement('a');
        link.className = 'predictive__item predictive__item--trailer';
        link.href = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'search?q=' + encodeURIComponent(match.regnr);
        link.id = `${this.results.id || 'predictive-results'}-option-${index}`;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');

        const badge = document.createElement('span');
        badge.className = 'predictive__trailer-badge';
        badge.setAttribute('aria-hidden', 'true');
        // The badge names the trailer's country, so it follows the resolved visitor
        // country (set by regnr-country.js) — never the language parameter above.
        badge.textContent =
          match.country || (document.documentElement && document.documentElement.dataset.regnrCountry) || '';
        link.appendChild(badge);

        const body = document.createElement('span');
        body.className = 'predictive__body';
        const title = document.createElement('span');
        title.className = 'predictive__title';
        title.textContent = match.regnr;
        body.appendChild(title);

        const meta = document.createElement('span');
        meta.className = 'predictive__meta';
        meta.textContent = window.ValerydFitment.describe(match.trailer, { braked: this.dataset.braked, unbraked: this.dataset.unbraked });
        body.appendChild(meta);
        link.appendChild(body);
        li.appendChild(link);
        return li;
      }

      partItem(match, index) {
        const li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        const link = document.createElement('a');
        link.className = 'predictive__item predictive__item--part';
        link.href = match.href;
        link.id = `${this.results.id || 'predictive-results'}-option-${index}`;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');

        const body = document.createElement('span');
        body.className = 'predictive__body';
        const title = document.createElement('span');
        title.className = 'predictive__title';
        title.textContent = match.term;
        body.appendChild(title);
        const meta = document.createElement('span');
        meta.className = 'predictive__meta';
        meta.textContent = match.meta;
        body.appendChild(meta);
        link.appendChild(body);
        li.appendChild(link);
        return li;
      }

      item(product, index) {
        const li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        const link = document.createElement('a');
        link.className = 'predictive__item';
        link.href = product.url;
        link.id = `${this.results.id || 'predictive-results'}-option-${index}`;
        link.setAttribute('role', 'option');
        link.setAttribute('aria-selected', 'false');

        const variant = product.variants && product.variants[0];
        const image = product.featured_image || (product.image ? { url: product.image } : null);

        if (image && image.url) {
          const img = document.createElement('img');
          img.className = 'predictive__img';
          img.src = image.url;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 44;
          img.height = 44;
          link.appendChild(img);
        }

        const body = document.createElement('span');
        body.className = 'predictive__body';

        const title = document.createElement('span');
        title.className = 'predictive__title';
        title.textContent = product.title;
        body.appendChild(title);

        const meta = document.createElement('span');
        meta.className = 'predictive__meta';
        if (variant && variant.sku) {
          const sku = document.createElement('span');
          sku.className = 'predictive__sku';
          sku.textContent = variant.sku;
          meta.appendChild(sku);
        }
        if (product.price != null) {
          const price = document.createElement('span');
          price.className = 'predictive__price';
          price.textContent = window.ValerydCards.money(product.price, this.locale, this.currency);
          meta.appendChild(price);
          const net = window.ValerydCards.net(product.price);
          if (net !== null && this.dataset.exclVat) {
            const excl = document.createElement('span');
            excl.className = 'predictive__excl-vat';
            excl.textContent = this.dataset.exclVat.split('%PRICE%').join(window.ValerydCards.money(net, this.locale, this.currency));
            meta.appendChild(excl);
          }
        }
        body.appendChild(meta);
        link.appendChild(body);
        li.appendChild(link);
        return li;
      }

      get options() {
        return Array.from(this.results.querySelectorAll('.predictive__item'));
      }

      setActive(index) {
        this.active = index;
        const opts = this.options;
        opts.forEach((option, optionIndex) => {
          const selected = optionIndex === index;
          option.classList.toggle('is-active', selected);
          option.setAttribute('aria-selected', selected ? 'true' : 'false');
        });

        const activeOption = opts[index];
        if (activeOption && activeOption.id) {
          this.input.setAttribute('aria-activedescendant', activeOption.id);
        } else {
          this.input.removeAttribute('aria-activedescendant');
        }
      }

      handleKey(event) {
        if (event.key === 'Escape') {
          this.close();
          return;
        }
        if (this.results.hidden) return;
        const opts = this.options;
        if (!opts.length) return;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const next = event.key === 'ArrowDown'
            ? (this.active + 1) % opts.length
            : (this.active - 1 + opts.length) % opts.length;
          this.setActive(next);
          opts[this.active].scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'Enter') {
          if (this.active > -1) {
            event.preventDefault();
            opts[this.active].click();
          } else {
            this.followAllResults(event);
          }
        }
      }

      open() {
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      }

      close() {
        this.results.hidden = true;
        this.input.setAttribute('aria-expanded', 'false');
        this.setActive(-1);
      }
    }
  );
}
